/**
 * LA POLÍTICA DE CONTRASEÑAS del portal, en UN solo archivo.
 *
 * ── DE DÓNDE SALE ESTE ARCHIVO (2026-08-26) ─────────────────────────────────
 * Se escribió el 2026-08-11 para el «portón de contraseña» que la usuaria pidió
 * ese día, quedó en la rama `feat/porton-de-contrasena` (PR #2, commit
 * `10d7abd`) y **nunca se mezcló ni se desplegó**: el riesgo declarado entonces
 * era que, con los 40 usuarios en `debe_cambiar = TRUE`, el día del despliegue
 * el portón los deja a todos fuera a la vez, la cuenta MASTER incluida.
 *
 * El 2026-08-26 la usuaria volvió a pedir «la opción de que los usuarios cambien
 * sus contraseñas», esta vez como puerta del recorrido guiado. Se rescató ESTE
 * archivo tal cual —es SU política, con sus palabras, y ya tenía sus 18 pruebas—
 * y lo consume la versión que sí está viva: `netlify/functions/auth-password.mts`
 * y `src/componentes/CambiarClave.tsx` (db/080).
 *
 * **Y ESE MISMO DÍA, más tarde, pidió el PORTÓN**: «que cada usuario cuando
 * ingrese a su cuenta el portal le solicite el cambio de clave». Así que la otra
 * mitad del PR #2 también está viva desde entonces — `requiereSesion()` cierra
 * los 51 endpoints y `Marco.tsx` tapa la aplicación entera hasta que la clave
 * sea propia. El código del 403 que lo dice está más abajo, en este archivo,
 * porque es la única pieza que comparten LOS TRES lados (servidor, portal y app
 * del teléfono).
 *
 * Este módulo lo importan LOS DOS LADOS:
 *  · el navegador (`src/componentes/CambiarClave.tsx` — es un COMPONENTE y no una
 *    página, porque el portón no tiene URL: lo devuelve `Marco` con un return
 *    temprano), para pintar la lista de requisitos que se va poniendo verde
 *    mientras el usuario teclea;
 *  · el servidor (`netlify/functions/auth-password.mts`, con
 *    `../../src/lib/clave.js`), que es EL QUE MANDA.
 *
 * Y desde el portón, un tercero que no puede importar TypeScript: la app del
 * teléfono (`public/movil/`, JavaScript a pelo servido tal cual). No se le
 * duplica la política — pide la lista de requisitos a `GET /api/auth/password`,
 * que la sirve DESDE AQUÍ, y el veredicto se lo da el POST. Lo único que repite
 * es la cadena del código de abajo, con un comentario que apunta a esta línea.
 *
 * Está en un archivo compartido y no duplicado a propósito: dos copias de una
 * política divergen sin dar error, y la forma en que divergen es siempre la
 * mala —la pantalla acepta lo que el servidor rechaza (el usuario ve un error
 * que la lista de requisitos jura que no existe) o, peor, la pantalla pone en
 * verde algo que el servidor guarda sin comprobar—. `tsconfig.json` mete `src`
 * y `netlify/functions` en el MISMO proyecto, así que compartir type-chequea.
 * Para que esto siga siendo importable desde una Netlify Function, el archivo
 * NO PUEDE tocar el DOM, ni React, ni `process`: solo JavaScript a secas.
 *
 * ── LA POLÍTICA, Y DE DÓNDE SALE ────────────────────────────────────────────
 * La pidió la usuaria el 2026-08-10, con estas palabras: «nada de 16
 * caracteres, minimo 8 caracteres entre mayusculas minusculas numeros y signo».
 * Así que: 8 mínimo, y las cuatro familias obligatorias.
 *
 * **EL SIGNO SE QUITÓ el 2026-08-26**, el día del portón, y no por comodidad de
 * nadie: al pedir el portón enumeró los requisitos otra vez y esta vez el signo
 * no estaba —«que contenga los parametros de seguridad basicos (mayuscula
 * minuscula numeros y al menos ocho caracteres»—. Se le preguntó explícitamente
 * si lo quitaba, con la contrapartida dicha (más fácil de recordar para los 15
 * asesores, más fácil de adivinar para un tercero) y contestó que sí. Así que
 * hoy son TRES familias obligatorias y el signo SIGUE SIENDO VÁLIDO, solo que no
 * obligatorio: `Abcdef1!` entra igual que `Abcdef12`.
 *
 * Ojo con el efecto secundario, que es el que sostiene todo lo demás: la clave
 * temporal `DH-XXXXXXXXXX` de `db/crear-usuarios.mjs` y `db/clave-usuario.mjs`
 * sigue sin poder pasar la política, pero AHORA por una sola razón —no lleva
 * minúsculas—, donde antes fallaba por dos. Si algún día alguien mete minúsculas
 * en ese alfabeto, el usuario podrá «cambiar» su clave por la misma temporal y
 * el portón se abrirá sin que nadie haya personalizado nada. Hay un test que lo
 * vigila (`clave.test.ts`, «las claves generadas por los scripts NO pasan»).
 *
 * NO se toca el mínimo de 16 de `db/clave-maestra.mjs`, y no es una
 * incoherencia: esa clave abre LAS 30 CUENTAS y la elige una sola persona que
 * la guarda en un gestor. Esta la teclea cada día cada uno de los 30 usuarios.
 * Son dos secretos con dos amenazas distintas y dos mínimos distintos.
 *
 * ── LOS TRES REQUISITOS QUE ELLA NO PIDIÓ Y ESTÁN AQUÍ ──────────────────────
 * Van declarados como reglas visibles, NUNCA como un rechazo sorpresa: los tres
 * se pintan en la pantalla junto a los tres suyos, así que el usuario ve por
 * qué no le sirve antes de pulsar nada.
 *
 *  1. TOPE DE 72 BYTES. `bcrypt` **ignora en silencio** todo lo que pase de 72
 *     bytes. Sin este tope, una clave de 80 caracteres se guarda a medias y
 *     entra igual escribiendo solo los primeros 72 — un debilitamiento invisible
 *     tanto para el usuario como para quien lea el código. Se miden BYTES y no
 *     caracteres porque en UTF-8 una `ñ` ocupa dos.
 *  2. NI ESPACIO AL PRINCIPIO NI AL FINAL. Es invisible en un campo de
 *     contraseña, sobrevive a un copiar-pegar y produce el peor fallo posible:
 *     «me acepta la clave al crearla y luego dice que es incorrecta». Los
 *     espacios de EN MEDIO sí se permiten: una frase con espacios es una buena
 *     contraseña y no engaña a nadie.
 *  3. NO CONTIENE EL NOMBRE DE USUARIO. `Audiovisual1!` cumple las tres
 *     familias y es la primera que alguien va a probar. Si molesta, se quita
 *     borrando su entrada de REGLAS y su caso del test.
 *
 * Lo que NO se comprueba aquí, porque necesita la base de datos, y va en el
 * endpoint: que la clave nueva **no sea la que ya tenía**. Sin esa comprobación
 * el usuario «cambia» su clave por la misma predeterminada, `debe_cambiar` pasa
 * a FALSE y el portal se queda convencido de que ya la personalizó.
 */

/**
 * EL CÓDIGO DEL 403 DEL PORTÓN. Vive en la política y no en `_lib/auth.ts`
 * porque lo tienen que decir igual TRES lados: el servidor que lo lanza
 * (`requiereSesion`), el portal que lo distingue de un «no tienes permiso»
 * cualquiera (`src/lib/api.ts`) y la app del teléfono, que lo repite a mano
 * porque es JavaScript suelto y no puede importar de aquí (`public/movil/api.js`,
 * con el comentario que apunta a esta línea).
 *
 * Va por un CAMPO PROPIO de la respuesta y no parseando el texto del error: el
 * mensaje está escrito para una persona y se puede reescribir cualquier día sin
 * saber que algo lo estaba leyendo. Un código no se reescribe por estilo.
 */
export const CODIGO_CAMBIO_REQUERIDO = 'CAMBIO_CLAVE_REQUERIDO';

/** Mínimo de caracteres. Lo fijó la usuaria: 8. */
export const MINIMO = 8;

/** Tope duro. Ver el punto 1 de la cabecera: `bcrypt` corta en 72 BYTES. */
export const MAXIMO_BYTES = 72;

/** Bytes que ocupa la clave en UTF-8, que es lo que `bcrypt` cuenta. */
export function bytesDe(clave: string): number {
  return new TextEncoder().encode(clave).length;
}

/**
 * Una regla de la política. `texto` es lo que se le pinta al usuario, así que
 * está redactado en segunda persona y sin jerga: es la MISMA cadena que sirve
 * de lista de requisitos en la pantalla y de mensaje de error en el servidor.
 * Una sola redacción para las dos cosas evita que digan cosas distintas.
 */
export interface ReglaClave {
  id: string;
  texto: string;
  cumple: (clave: string, username: string) => boolean;
}

/*
 * Se usan clases Unicode (`\p{Lu}`, `\p{Ll}`) y no `[A-Z]`/`[a-z]` para que una
 * `Ñ` cuente como mayúscula y una `é` como minúscula. Con `[A-Z]` un usuario
 * que escriba `Ñandú` en su clave vería «te falta una mayúscula» teniendo una
 * delante, y no habría forma de que entendiera por qué.
 */
const MAYUSCULA = /\p{Lu}/u;
const MINUSCULA = /\p{Ll}/u;
const NUMERO = /\p{Nd}/u;
/* Aquí vivía SIGNO —«lo que no es letra, ni número, ni espacio»— hasta el
 * 2026-08-26. Se fue con su regla (ver la cabecera). No se deja comentada por
 * costumbre: se deja anotada porque lo que hay que recordar es que el espacio
 * quedaba FUERA de esa definición a propósito, y esa decisión sigue viva en la
 * regla `bordes` y en el test de los espacios de en medio. */

export const REGLAS: ReglaClave[] = [
  {
    id: 'largo',
    texto: `Al menos ${MINIMO} caracteres`,
    cumple: (c) => [...c].length >= MINIMO,
  },
  {
    id: 'mayuscula',
    texto: 'Una letra mayúscula',
    cumple: (c) => MAYUSCULA.test(c),
  },
  {
    id: 'minuscula',
    texto: 'Una letra minúscula',
    cumple: (c) => MINUSCULA.test(c),
  },
  {
    id: 'numero',
    texto: 'Un número',
    cumple: (c) => NUMERO.test(c),
  },
  {
    id: 'tope',
    texto: `No más de ${MAXIMO_BYTES} bytes (las tildes y la ñ cuentan doble)`,
    cumple: (c) => bytesDe(c) <= MAXIMO_BYTES,
  },
  {
    id: 'bordes',
    texto: 'Sin espacios al principio ni al final',
    cumple: (c) => c === c.trim(),
  },
  {
    id: 'sinUsuario',
    texto: 'No contiene tu nombre de usuario',
    /* Con la clave vacía la regla se da por cumplida: si no, la pantalla abre
     * con este requisito en rojo antes de que el usuario escriba una letra, y
     * un requisito en rojo sin haber hecho nada se lee como un error. */
    cumple: (c, username) => {
      if (!c) return true;
      const u = username.trim().toLowerCase();
      if (u.length < 4) return true;   // un usuario cortísimo prohibiría demasiado
      return !c.toLowerCase().includes(u);
    },
  },
];

/** Las reglas que la clave NO cumple. Vacío = sirve. */
export function reglasIncumplidas(clave: string, username: string): ReglaClave[] {
  return REGLAS.filter((r) => !r.cumple(clave, username));
}

/**
 * `null` si la clave sirve; si no, UNA frase con todo lo que le falta, lista
 * para devolver al navegador. El servidor la usa como el mensaje del 400.
 */
export function porQueNoSirve(clave: string, username: string): string | null {
  const faltan = reglasIncumplidas(clave, username);
  if (faltan.length === 0) return null;
  if (faltan.length === 1) return `La contraseña no cumple: ${faltan[0]!.texto.toLowerCase()}.`;
  return 'La contraseña no cumple: '
    + faltan.map((r) => r.texto.toLowerCase()).join(' · ') + '.';
}

/** Atajo legible para el `disabled` del botón de la pantalla. */
export function claveSirve(clave: string, username: string): boolean {
  return reglasIncumplidas(clave, username).length === 0;
}
